/**
 * ACORDE — Nota como asistente (snippets/acorde-nota-asistente.liquid).
 *
 * Botón flotante + globo con el mensaje de la página + ventana de conversación.
 *
 * Cómo responde Nota hoy (sin IA conectada):
 * - Opciones para tocar → `accion()`: guiones cortos que usan datos reales de la tienda
 *   (metacampos del producto, productos de una colección, recomendados de Shopify, catálogo del quiz).
 * - Texto escrito → `entender()`: palabras clave que llevan a esos mismos guiones y, si no, el buscador
 *   de la tienda. Si nada sirve, lo dice y ofrece opciones.
 *
 * Dónde se conecta la IA: `preguntarALaIA()`. Si el ajuste «Dirección del asesor con IA» tiene una
 * dirección, el texto libre se envía allí con el contexto de la página y el historial. Ese servicio
 * (no el tema) guarda la clave y debe responder { texto, opciones?, productos? }.
 */
const CLAVE_HILO = 'acorde_nota_hilo';
const INTENSIDAD = { 1: 'suave', 2: 'equilibrada', 3: 'intensa' };

const escapar = (texto) =>
  String(texto ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const pesos = (centavos) => '$' + Math.round(centavos / 100).toLocaleString('es-CO');

const espera = (ms) => new Promise((listo) => setTimeout(listo, ms));

class AcordeNotaAsistente extends HTMLElement {
  connectedCallback() {
    this.ctx = JSON.parse(this.querySelector('[data-contexto]').textContent);
    this.globo = this.querySelector('[data-globo]');
    this.panel = this.querySelector('[data-panel]');
    this.hilo = this.querySelector('[data-hilo]');
    this.opciones = this.querySelector('[data-opciones]');
    this.form = this.querySelector('[data-form]');
    this.campo = this.form.elements.mensaje;
    this.avatar = this.querySelector('[data-avatar] acorde-nota');
    this.mini = this.querySelector('.acorde-asistente__boton acorde-nota');
    this.historial = [];

    this.addEventListener('click', (evento) => this.alTocar(evento));
    this.form.addEventListener('submit', (evento) => this.alEnviar(evento));
    this.campo.addEventListener('input', () => this.gesto(this.campo.value ? 'escuchando' : 'sonriendo'));
    this.panel.addEventListener('close', () => this.alCerrar());
    this.panel.addEventListener('keydown', (evento) => {
      if (evento.key !== 'Escape') return;
      evento.stopPropagation(); // que no lo tome el cajón del tema
      this.panel.close();
    });

    // Al cambiar de página, la conversación se guarda para seguirla en la siguiente.
    window.addEventListener('pagehide', () => this.empezo && this.guardar());

    this.programarGlobo();
  }

  // ---------------------------------------------------------------- globo

  programarGlobo() {
    if (!this.globo || sessionStorage.getItem(`acorde_nota_globo_${this.ctx.tipo}`)) return;
    const segundos = parseFloat(this.dataset.espera || '3');
    this.relojGlobo = setTimeout(() => {
      if (this.panel.open) return;
      this.globo.hidden = false;
      this.mini?.decir?.('sonriendo', 2600);
    }, segundos * 1000);
  }

  ocultarGlobo() {
    clearTimeout(this.relojGlobo);
    if (!this.globo) return;
    this.globo.hidden = true;
    try {
      sessionStorage.setItem(`acorde_nota_globo_${this.ctx.tipo}`, '1');
    } catch {
      // Sin almacenamiento, el globo volverá a salir en la siguiente página: no es grave.
    }
  }

  // ---------------------------------------------------------------- abrir y cerrar

  abrir() {
    this.ocultarGlobo();
    if (this.panel.open) return;
    // En celular es una hoja que ocupa la pantalla; en computador, una ventana que deja seguir navegando.
    if (matchMedia('(min-width: 750px)').matches) this.panel.show();
    else this.panel.showModal();
    this.querySelector('.acorde-asistente__boton').hidden = true;
    if (!this.empezo) {
      this.empezo = true;
      this.restaurar() || this.saludar();
    }
    this.bajar();
  }

  alCerrar() {
    this.querySelector('.acorde-asistente__boton').hidden = false;
    this.guardar();
  }

  alTocar(evento) {
    const boton = evento.target.closest('button, a');
    if (!boton) return;
    if (boton.matches('[data-cerrar-globo]')) return this.ocultarGlobo();
    if (boton.matches('[data-abrir]')) return this.abrir();
    if (boton.matches('[data-cerrar]')) return this.panel.close();
    if (boton.matches('[data-accion]')) {
      evento.preventDefault();
      const dato = boton.dataset.dato ? JSON.parse(boton.dataset.dato) : undefined;
      if (boton.closest('[data-opciones]')) this.yo(boton.textContent.trim());
      this.accion(boton.dataset.accion, dato);
    }
  }

  async alEnviar(evento) {
    evento.preventDefault();
    const texto = this.campo.value.trim();
    if (!texto) return;
    this.campo.value = '';
    this.yo(texto);
    await this.entender(texto);
  }

  // ---------------------------------------------------------------- piezas de la conversación

  gesto(estado) {
    this.avatar?.setAttribute('estado', estado);
  }

  bajar() {
    this.hilo.scrollTop = this.hilo.scrollHeight;
    requestAnimationFrame(() => (this.hilo.scrollTop = this.hilo.scrollHeight));
  }

  agregar(html, clase = 'acorde-asistente__msj') {
    const nodo = document.createElement('div');
    nodo.className = clase;
    nodo.innerHTML = html;
    this.hilo.appendChild(nodo);
    this.bajar();
    return nodo;
  }

  yo(texto) {
    this.poner([]);
    this.historial.push({ de: 'cliente', texto });
    this.agregar(escapar(texto), 'acorde-asistente__msj acorde-asistente__msj--yo');
  }

  /** Nota «escribe» un momento y luego habla. La pausa crece con el largo del mensaje. */
  async decir(texto, gesto = 'sonriendo') {
    this.gesto('pensando');
    const puntos = this.agregar('<i></i><i></i><i></i>', 'acorde-asistente__msj acorde-asistente__msj--puntos');
    await espera(Math.min(1400, 420 + texto.length * 9));
    puntos.remove();
    this.historial.push({ de: 'nota', texto });
    this.agregar(escapar(texto));
    this.gesto(gesto);
  }

  /** Opciones para tocar. Cada una: { texto, accion, dato?, imagen? } */
  poner(lista) {
    this.opciones.innerHTML = lista
      .map(
        (o) =>
          `<button type="button" class="acorde-asistente__opcion" data-accion="${escapar(o.accion)}"${
            o.dato !== undefined ? ` data-dato='${escapar(JSON.stringify(o.dato))}'` : ''
          }>${o.imagen ? `<img src="${escapar(o.imagen)}" alt="" loading="lazy">` : ''}${escapar(o.texto)}</button>`
      )
      .join('');
    this.bajar();
  }

  /** Carrusel de perfumes. Cada uno: { titulo, marca, url, imagen, precio (centavos), variante } */
  tarjetas(productos) {
    const html = productos
      .map(
        (p) => `<article class="acorde-asistente__tarjeta">
          <a href="${escapar(p.url)}">
            ${p.imagen ? `<img src="${escapar(p.imagen)}" alt="${escapar(p.titulo)}" loading="lazy" width="158" height="198">` : ''}
            <div><small>${escapar(p.marca)}</small><b>${escapar(p.titulo)}</b>${p.precio ? `<span>${pesos(p.precio)}</span>` : ''}</div>
          </a>
          ${p.variante ? `<button type="button" data-accion="agregar" data-dato='${escapar(JSON.stringify({ id: p.variante, titulo: p.titulo }))}'>Agregar</button>` : ''}
        </article>`
      )
      .join('');
    this.agregar(`<div class="acorde-asistente__tarjetas">${html}</div>`, 'acorde-asistente__bloque');
    this.gesto('recomendando');
  }

  // ---------------------------------------------------------------- datos de la tienda

  async json(url, opciones) {
    const respuesta = await fetch(url, { headers: { Accept: 'application/json' }, ...opciones });
    if (!respuesta.ok) throw new Error(String(respuesta.status));
    return respuesta.json();
  }

  /** Productos de una colección, en el orden que tenga configurado la colección. */
  async deColeccion(url, cuantos = 5) {
    const datos = await this.json(`${url}/products.json?limit=${cuantos}`);
    return datos.products.map((p) => ({
      titulo: p.title,
      marca: p.vendor,
      url: `/products/${p.handle}`,
      imagen: p.images[0]?.src ? `${p.images[0].src}${p.images[0].src.includes('?') ? '&' : '?'}width=320` : '',
      precio: Math.round(parseFloat(p.variants[0]?.price || '0') * 100),
      variante: p.variants.find((v) => v.available)?.id,
    }));
  }

  deShopify(p) {
    return {
      titulo: p.title,
      marca: p.vendor,
      url: p.url,
      imagen: p.featured_image ? `${p.featured_image.url || p.featured_image}${String(p.featured_image.url || p.featured_image).includes('?') ? '&' : '?'}width=320` : '',
      precio: typeof p.price === 'number' ? p.price : Math.round(parseFloat(p.price || '0') * 100),
      variante: p.variants?.find?.((v) => v.available)?.id,
    };
  }

  /** El catálogo y las familias salen de la página del quiz: una sola fuente para los dos. */
  async catalogo() {
    if (this.cat) return this.cat;
    const html = await (await fetch(this.ctx.rutas.quiz)).text();
    const pagina = new DOMParser().parseFromString(html, 'text/html');
    const productos = JSON.parse(pagina.querySelector('acorde-quiz [data-productos]')?.textContent || '[]');
    const conProductos = new Set(productos.flatMap((p) => p.f));
    const familias = [...pagina.querySelectorAll('[data-paso="familia"] [data-valor]')]
      .filter((o) => conProductos.has(o.dataset.valor))
      .map((o) => ({ valor: o.dataset.valor, nombre: o.dataset.etiqueta, imagen: o.querySelector('img')?.getAttribute('src') || '' }));
    this.cat = { productos, familias };
    return this.cat;
  }

  // ---------------------------------------------------------------- guiones

  async saludar() {
    const { tipo, producto, coleccion, carrito, busqueda } = this.ctx;
    if (tipo === 'product' && producto) {
      await this.decir(`${this.ctx.saludo} Veo que estás mirando ${producto.titulo}. ¿Qué quieres saber?`);
      return this.poner(this.opcionesDeProducto());
    }
    if (tipo === 'collection' && coleccion) {
      await this.decir(`${this.ctx.saludo} Estás en ${coleccion.titulo}. ¿Te muestro algunos para empezar?`);
      return this.poner([
        { texto: 'Sí, muéstrame 5', accion: 'coleccion' },
        { texto: 'Ayúdame a elegir', accion: 'elegir' },
        { texto: 'Busco un regalo', accion: 'regalo' },
      ]);
    }
    if (tipo === 'cart') {
      await this.decir(`${this.ctx.saludo} ${carrito?.cantidad ? '¿Te ayudo a terminar tu pedido?' : 'Tu carrito está vacío. ¿Te ayudo a elegir?'}`);
      return this.poner(
        carrito?.cantidad
          ? [
              { texto: '¿Cómo es el envío?', accion: 'envio' },
              { texto: '¿Cómo pago?', accion: 'pago' },
              { texto: 'Hablar con una persona', accion: 'persona' },
            ]
          : this.opcionesGenerales()
      );
    }
    if (tipo === 'search' && busqueda && !busqueda.resultados) {
      await this.decir(`${this.ctx.saludo} No encontramos «${busqueda.terminos}». Cuéntame qué buscas y lo miramos juntos.`, 'lo-siento');
      return this.poner(this.opcionesGenerales());
    }
    await this.decir(`${this.ctx.saludo} Cuéntame qué buscas y te ayudo a encontrarlo.`);
    this.poner(this.opcionesGenerales());
  }

  opcionesGenerales() {
    return [
      { texto: 'Ayúdame a elegir', accion: 'elegir' },
      { texto: 'Busco un regalo', accion: 'regalo' },
      { texto: 'Muéstrame algunos', accion: 'destacados' },
      { texto: 'Tengo una duda', accion: 'dudas' },
    ];
  }

  opcionesDeProducto() {
    const p = this.ctx.producto;
    const lista = [];
    if (p.niveles.some((n) => n.notas.length)) lista.push({ texto: '¿Cómo huele?', accion: 'huele' });
    if (p.duracion) lista.push({ texto: '¿Cuánto dura?', accion: 'dura' });
    if (p.ideal?.length) lista.push({ texto: '¿Para cuándo es?', accion: 'cuando' });
    lista.push({ texto: 'Ver parecidos', accion: 'parecidos' });
    if (p.disponible) lista.push({ texto: 'Agregar al carrito', accion: 'agregar', dato: { id: p.variante, titulo: p.titulo } });
    return lista;
  }

  async accion(nombre, dato) {
    const p = this.ctx.producto;
    try {
      switch (nombre) {
        case 'huele': {
          await this.decir(p.resumen || p.personalidad || `Estas son las notas de ${p.titulo}.`);
          const niveles = p.niveles
            .filter((n) => n.notas.length)
            .map(
              (n) =>
                `<div class="acorde-asistente__nivel"><b>${escapar(n.titulo)}</b><ul>${n.notas
                  .map((x) => `<li>${x.imagen ? `<img src="${escapar(x.imagen)}" alt="" loading="lazy">` : ''}${escapar(x.nombre)}</li>`)
                  .join('')}</ul></div>`
            )
            .join('');
          this.agregar(`<div class="acorde-asistente__niveles">${niveles}</div>`, 'acorde-asistente__bloque');
          await this.decir('La salida es lo primero que se siente; el fondo es lo que queda en la piel.');
          return this.poner(this.opcionesDeProducto().filter((o) => o.accion !== 'huele'));
        }
        case 'dura': {
          await this.decir(`En nuestra escala de 1 a 10, ${p.titulo} tiene duración ${p.duracion} y estela ${p.estela}. Es de intensidad ${INTENSIDAD[p.intensidad] || 'media'}.`);
          this.agregar(
            `<div class="acorde-asistente__medidas">
              <div class="acorde-asistente__medida"><span>Duración</span><i style="--v:${p.duracion * 10}%"></i><b>${p.duracion}/10</b></div>
              <div class="acorde-asistente__medida"><span>Estela</span><i style="--v:${p.estela * 10}%"></i><b>${p.estela}/10</b></div>
            </div>`,
            'acorde-asistente__bloque'
          );
          await this.decir('La duración cambia con la piel y el clima: en calor se siente más, pero se va antes.');
          return this.poner(this.opcionesDeProducto().filter((o) => o.accion !== 'dura'));
        }
        case 'cuando': {
          await this.decir(`${p.titulo} va mejor para: ${[].concat(p.ideal).join(', ').toLowerCase()}.`);
          return this.poner(this.opcionesDeProducto().filter((o) => o.accion !== 'cuando'));
        }
        case 'parecidos': {
          const datos = await this.json(`${this.ctx.rutas.recomendados}.json?product_id=${p.id}&limit=5&intent=related`);
          if (!datos.products?.length) throw new Error('sin parecidos');
          await this.decir('Si te gusta este, mira estos:', 'recomendando');
          this.tarjetas(datos.products.map((x) => this.deShopify(x)));
          return this.poner([{ texto: 'Ayúdame a elegir', accion: 'elegir' }, { texto: 'Tengo una duda', accion: 'dudas' }]);
        }
        case 'agregar': {
          await this.json(this.ctx.rutas.agregar, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: dato.id, quantity: 1 }),
          });
          document.dispatchEvent(new CustomEvent('acorde:carrito', { bubbles: true }));
          await this.decir(`Listo, ${dato.titulo} ya está en tu carrito.`, 'riendo');
          return this.poner([
            { texto: 'Ver mi carrito', accion: 'ir', dato: this.ctx.rutas.carrito },
            { texto: 'Seguir mirando', accion: 'destacados' },
          ]);
        }
        case 'coleccion':
        case 'destacados': {
          const url = nombre === 'coleccion' ? this.ctx.coleccion.url : this.ctx.rutas.destacados;
          const productos = await this.deColeccion(url);
          if (!productos.length) throw new Error('sin productos');
          await this.decir(nombre === 'coleccion' ? `Aquí van ${productos.length} de ${this.ctx.coleccion.titulo}:` : 'Mira estos para empezar:', 'recomendando');
          this.tarjetas(productos);
          return this.poner([
            { texto: 'Ayúdame a elegir', accion: 'elegir' },
            { texto: 'Ver todos', accion: 'ir', dato: url },
          ]);
        }
        case 'regalo':
        case 'elegir': {
          this.eleccion = { regalo: nombre === 'regalo' };
          await this.decir(nombre === 'regalo' ? 'Qué buen detalle. ¿Para quién es?' : 'Con gusto. ¿Qué tipo de fragancia buscas?', 'preguntando');
          return this.poner([
            { texto: nombre === 'regalo' ? 'Para ella' : 'Femenina', accion: 'elige-quien', dato: 'Mujer' },
            { texto: nombre === 'regalo' ? 'Para él' : 'Masculina', accion: 'elige-quien', dato: 'Hombre' },
            { texto: 'Me da igual', accion: 'elige-quien', dato: '' },
          ]);
        }
        case 'elige-quien': {
          this.eleccion = { ...this.eleccion, quien: dato };
          const { familias } = await this.catalogo();
          await this.decir(this.eleccion.regalo ? '¿Qué aromas le gustan?' : '¿Qué aromas te atraen?', 'preguntando');
          return this.poner([
            ...familias.map((f) => ({ texto: f.nombre, accion: 'elige-familia', dato: f.valor, imagen: f.imagen })),
            { texto: 'No estoy seguro', accion: 'elige-familia', dato: '' },
          ]);
        }
        case 'elige-familia': {
          const { productos, familias } = await this.catalogo();
          const quien = this.eleccion?.quien;
          const elegibles = productos.filter((x) => !quien || x.q === quien || x.q === 'Unisex');
          const puntuados = elegibles
            .map((x, orden) => ({ x, orden, puntos: dato ? (x.f[0] === dato ? 2 : x.f.includes(dato) ? 1 : 0) : 0 }))
            .filter((r) => !dato || r.puntos > 0)
            .sort((a, b) => b.puntos - a.puntos || a.orden - b.orden)
            .slice(0, 4);
          if (!puntuados.length) throw new Error('sin coincidencias');
          const nombreFamilia = familias.find((f) => f.valor === dato)?.nombre;
          await this.decir(nombreFamilia ? `De la familia ${nombreFamilia.toLowerCase()}, estos encajan:` : 'Estos son un buen punto de partida:', 'recomendando');
          this.tarjetas(puntuados.map(({ x }) => ({ titulo: x.t, marca: x.m, url: `/products/${x.h}`, precio: x.p * 100, imagen: '' })));
          this.completarImagenes();
          await this.decir('Si quieres una recomendación más fina, el quiz toma un minuto.');
          return this.poner([
            { texto: 'Hacer el quiz', accion: 'ir', dato: this.ctx.rutas.quiz },
            { texto: 'Probar con otros aromas', accion: 'elige-quien', dato: quien },
          ]);
        }
        case 'dudas': {
          await this.decir('Claro. ¿Sobre qué?', 'preguntando');
          return this.poner([
            { texto: 'Envío', accion: 'envio' },
            { texto: 'Pago', accion: 'pago' },
            { texto: 'Hablar con una persona', accion: 'persona' },
          ]);
        }
        case 'envio':
        case 'pago': {
          const texto = this.ctx.textos[nombre];
          if (texto) await this.decir(texto);
          else await this.decir('Prefiero no darte un dato equivocado: eso te lo confirma una persona del equipo.', 'lo-siento');
          return this.poner([{ texto: 'Hablar con una persona', accion: 'persona' }, ...this.opcionesGenerales().slice(0, 2)]);
        }
        case 'persona': {
          if (this.ctx.whatsapp) {
            await this.decir('Te paso con alguien del equipo por WhatsApp.');
            window.open(`https://wa.me/${this.ctx.whatsapp}?text=${encodeURIComponent('Hola, ACORDE. Vengo de hablar con Nota.')}`, '_blank', 'noopener');
          } else {
            await this.decir('Todavía no tenemos conectado el chat con el equipo. Mientras tanto, te ayudo con lo que pueda.', 'lo-siento');
          }
          return this.poner(this.opcionesGenerales());
        }
        case 'ir':
          this.guardar();
          return (location.href = dato);
        default:
          return this.noEntendi();
      }
    } catch {
      return this.noEntendi();
    }
  }

  /** Las tarjetas que salen del catálogo del quiz no traen foto: se pide a la tienda. */
  async completarImagenes() {
    const pendientes = [...this.hilo.querySelectorAll('.acorde-asistente__tarjeta a:not(:has(img))')];
    await Promise.all(
      pendientes.map(async (enlace) => {
        try {
          const datos = await this.json(`${enlace.getAttribute('href')}.js`);
          const foto = datos.featured_image;
          if (foto) enlace.insertAdjacentHTML('afterbegin', `<img src="${escapar(foto)}${foto.includes('?') ? '&' : '?'}width=320" alt="${escapar(datos.title)}" loading="lazy" width="158" height="198">`);
          const variante = datos.variants.find((v) => v.available);
          if (variante) {
            enlace.insertAdjacentHTML('afterend', `<button type="button" data-accion="agregar" data-dato='${escapar(JSON.stringify({ id: variante.id, titulo: datos.title }))}'>Agregar</button>`);
          }
        } catch {
          // Sin foto, la tarjeta sigue sirviendo como enlace.
        }
      })
    );
  }

  async noEntendi() {
    await this.decir('Eso todavía no lo sé responder bien. ¿Te ayudo con alguna de estas?', 'lo-siento');
    this.poner(this.ctx.producto ? this.opcionesDeProducto() : this.opcionesGenerales());
  }

  // ---------------------------------------------------------------- texto escrito

  async entender(texto) {
    if (this.ctx.api) {
      const respondio = await this.preguntarALaIA(texto);
      if (respondio) return;
    }
    const t = texto.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const hay = (patron) => patron.test(t);
    const p = this.ctx.producto;
    if (hay(/\b(hola|buenas|buenos dias|hey)\b/) && t.length < 24) {
      await this.decir('Hola. ¿En qué te ayudo?');
      return this.poner(p ? this.opcionesDeProducto() : this.opcionesGenerales());
    }
    if (hay(/gracias/)) {
      await this.decir('Con gusto. Aquí estoy si necesitas algo más.', 'riendo');
      return this.poner(p ? this.opcionesDeProducto() : this.opcionesGenerales());
    }
    if (hay(/envio|llega|domicilio|despacho|entrega/)) return this.accion('envio');
    if (hay(/pago|pagar|tarjeta|pse|nequi|transferencia|cuotas/)) return this.accion('pago');
    if (hay(/persona|humano|asesor|whatsapp|llamar/)) return this.accion('persona');
    if (hay(/regalo|regalar|cumple|aniversario/)) return this.accion('regalo');
    if (p && hay(/dura|duracion|fijacion|estela|proyec/)) return this.accion('dura');
    if (p && hay(/huele|olor|aroma|notas/)) return this.accion('huele');
    if (p && hay(/cuando|ocasion|clima|oficina|noche|dia/)) return this.accion('cuando');
    if (p && hay(/parecid|similar|otro como/)) return this.accion('parecidos');
    if (hay(/recomi|recomend|elegir|no se cual|ayuda|sugier/)) return this.accion('elegir');

    // ¿Nombró una familia olfativa? («algo dulce», «uno cítrico», «amaderado para él»)
    try {
      const { familias } = await this.catalogo();
      const raiz = (palabra) => palabra.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/(ada|ado|ica|ico|a|o|al|es|s)$/, '');
      const nombrada = familias.find((f) => raiz(f.nombre).length > 2 && t.includes(raiz(f.nombre)));
      if (nombrada) {
        this.eleccion = { quien: hay(/\b(hombre|el|masculin|caballero|novio|papa|esposo)\b/) ? 'Hombre' : hay(/\b(mujer|ella|femenin|dama|novia|mama|esposa)\b/) ? 'Mujer' : '' };
        return this.accion('elige-familia', nombrada.valor);
      }
    } catch {
      // Sin catálogo, se sigue con el buscador.
    }

    // Si no es una intención conocida, se busca en la tienda: marcas, perfumes y notas.
    // Primero la frase completa; si no hay nada, palabra por palabra (las más largas primero).
    const intentos = [texto, ...t.split(/\s+/).filter((w) => w.length > 4).sort((a, b) => b.length - a.length).slice(0, 3)];
    for (const consulta of intentos) try {
      const datos = await this.json(`${this.ctx.rutas.buscar}.json?q=${encodeURIComponent(consulta)}&resources[type]=product&resources[limit]=5`);
      const productos = datos.resources?.results?.products || [];
      if (productos.length) {
        await this.decir(`Esto fue lo que encontré para «${consulta}»:`, 'recomendando');
        this.tarjetas(productos.map((x) => ({ titulo: x.title, marca: x.vendor, url: x.url, imagen: x.image ? `${x.image}${x.image.includes('?') ? '&' : '?'}width=320` : '', precio: Math.round(parseFloat(x.price || '0') * 100) })));
        this.completarImagenes();
        return this.poner([{ texto: 'Ayúdame a elegir', accion: 'elegir' }, { texto: 'Tengo una duda', accion: 'dudas' }]);
      }
    } catch {
      // Sin buscador, se cae al mensaje de abajo.
    }
    return this.noEntendi();
  }

  /**
   * Punto de conexión con el asesor de IA. El servicio recibe el mensaje, el contexto de la página
   * y el historial, y responde { texto, opciones?: [{texto, accion, dato?}], productos?: [...] }.
   */
  async preguntarALaIA(mensaje) {
    this.gesto('pensando');
    const puntos = this.agregar('<i></i><i></i><i></i>', 'acorde-asistente__msj acorde-asistente__msj--puntos');
    try {
      const { textos, rutas, api, ...pagina } = this.ctx;
      const datos = await this.json(api, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ mensaje, pagina, historial: this.historial.slice(-12) }),
      });
      puntos.remove();
      if (!datos?.texto) return false;
      this.historial.push({ de: 'nota', texto: datos.texto });
      this.agregar(escapar(datos.texto));
      if (datos.productos?.length) this.tarjetas(datos.productos);
      else this.gesto('sonriendo');
      this.poner(datos.opciones || []);
      return true;
    } catch {
      puntos.remove();
      return false;
    }
  }

  // ---------------------------------------------------------------- memoria de la visita

  guardar() {
    try {
      sessionStorage.setItem(CLAVE_HILO, JSON.stringify({ hilo: this.hilo.innerHTML, historial: this.historial.slice(-20) }));
    } catch {
      // La conversación simplemente no se recordará en la siguiente página.
    }
  }

  /** Si ya venían conversando en otra página, la conversación sigue donde iba. */
  restaurar() {
    try {
      const guardado = JSON.parse(sessionStorage.getItem(CLAVE_HILO) || 'null');
      if (!guardado?.hilo) return false;
      this.hilo.innerHTML = guardado.hilo;
      this.historial = guardado.historial || [];
      this.hilo.querySelectorAll('.acorde-asistente__msj--puntos').forEach((n) => n.remove());
      this.poner(this.ctx.producto ? this.opcionesDeProducto() : this.opcionesGenerales());
      return true;
    } catch {
      return false;
    }
  }
}

if (!customElements.get('acorde-nota-asistente')) {
  customElements.define('acorde-nota-asistente', AcordeNotaAsistente);
}
